import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { State } from 'src/app/store';
import { profileSelector } from 'src/app/user/store/user.selectors';
import { Profile } from 'src/app/user/user.model';
import { Router } from '@angular/router';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { Request } from 'src/app/request-v2/models/request.model';
import { RequestsV2ImplService } from '../../../../services/requests-v2.impl.service';
import { AdminUser } from '../../../shared/subtask-assignee-menu/subtask-assignee-menu.component';

interface AdminSearchResult {
    id?: string;
    _id?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    picture?: string;
}

@Component({
    selector: 'app-request-internal-note',
    templateUrl: './request-internal-note.component.html',
    styleUrls: ['./request-internal-note.component.scss'],
})
export class RequestInternalNoteComponent implements OnInit, OnDestroy {
    @Input() request: Request;
    @Input() canEdit = false;
    @Output() saveRequest = new EventEmitter<Request>();
    @Output() requestChanged = new EventEmitter<Request>();
    @ViewChild('noteInput') noteInput!: ElementRef<HTMLDivElement>;

    isLoading = false;
    currentUser$: Observable<Profile | undefined>;
    currentLocale: string;
    currentLang: string;

    // Mention state
    showMentionDropdown = false;
    mentionSearchResults: AdminUser[] = [];
    mentionLoading = false;
    private savedRange: Range | null = null;
    private blurTimeout: ReturnType<typeof setTimeout>;
    private mentionSearchSubject = new Subject<string>();

    // Edit state
    editingNoteId: string | null = null;
    isEditLoading = false;
    currentUserId: string;
    private _editInput: ElementRef<HTMLDivElement>;

    @ViewChild('editInput') set editInputRef(el: ElementRef<HTMLDivElement>) {
        if (el && this.editingNoteId && !this._editInput) {
            const note = this.internalNotes.find((n) => n._id === this.editingNoteId);
            if (note) {
                el.nativeElement.innerHTML = this.descriptionToEditableHtml(note.description);
                requestAnimationFrame(() => {
                    el.nativeElement.focus();
                    const sel = window.getSelection();
                    if (sel) {
                        const range = document.createRange();
                        range.selectNodeContents(el.nativeElement);
                        range.collapse(false);
                        sel.removeAllRanges();
                        sel.addRange(range);
                    }
                });
            }
            this._editInput = el;
        }
    }

    private langChangeSubscription: Subscription;
    private destroy$ = new Subject<void>();

    constructor(
        private requestsService: RequestsV2ImplService,
        private store: Store<State>,
        private translateService: TranslateService,
        private userService: UserService,
        private router: Router,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.currentUser$ = this.store.select(profileSelector);
        this.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((profile) => {
            this.currentUserId = profile?.id;
        });
        this.setLocale(this.translateService.currentLang);
        this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.setLocale(event.lang);
        });

        this.mentionSearchSubject.pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((query) => this.searchMentionAdmins(query));
    }

    ngOnDestroy(): void {
        if (this.langChangeSubscription) {
            this.langChangeSubscription.unsubscribe();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setLocale(lang: string): void {
        this.currentLocale = lang === 'fr' ? 'fr-CA' : 'en-CA';
        this.currentLang = lang;
    }

    get internalNotes() {
        return this.request?.internalNotes || [];
    }

    onMentionClick(event: MouseEvent): void {
        const { target } = event;
        if (target instanceof HTMLElement && target.classList.contains('mention')) {
            const { userId } = target.dataset;
            if (userId) {
                const url = this.router.serializeUrl(this.router.createUrlTree(['/user-update', userId]));
                window.open(url, '_blank');
            }
        }
    }

    hasNote(): boolean {
        const div = this.noteInput?.nativeElement;
        return !!div && div.innerText.trim().length > 0;
    }

    sendNote(): void {
        if (!this.hasNote()) return;

        const noteToSend = this.serializeContent();
        this.isLoading = true;
        this.requestsService.createRequestInternalNote(this.request._id, noteToSend).subscribe((updatedRequest) => {
            this.isLoading = false;
            this.request = updatedRequest;
            this.noteInput.nativeElement.innerHTML = '';
            this.requestChanged.emit(updatedRequest);
            this.saveRequest.emit(updatedRequest);
        });
    }

    // Serialize div content: text nodes as-is, mention chips as @[id], <br>/<div> as newlines
    private serializeContent(): string {
        return this.serializeNode(this.noteInput.nativeElement).trim();
    }

    private serializeNode(node: Node): string {
        let result = '';
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                result += (child.textContent || '').replace(/\u00A0/g, ' ');
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                const el = child as HTMLElement;
                if (el.classList.contains('mention-chip')) {
                    result += `@[${el.dataset.id}]`;
                } else if (el.tagName === 'BR') {
                    result += '\n';
                } else if (el.tagName === 'DIV' || el.tagName === 'P') {
                    // The block is the line break; drop the <br> Chrome pads empty lines with (<div><br></div>)
                    result += `\n${this.serializeNode(el).replace(/\n$/, '')}`;
                } else {
                    result += this.serializeNode(el);
                }
            }
        });
        return result;
    }

    isNoteOwner(note: any): boolean {
        return !!note._id && note.user?._id === this.currentUserId;
    }

    startEdit(note: any): void {
        this._editInput = null;
        this.editingNoteId = note._id;
    }

    cancelEdit(): void {
        this.editingNoteId = null;
        this.isEditLoading = false;
        this._editInput = null;
    }

    saveEdit(): void {
        if (!this._editInput || !this.editingNoteId) return;

        const serialized = this.serializeNode(this._editInput.nativeElement).trim();
        if (!serialized) return;

        this.isEditLoading = true;
        this.requestsService.editInternalNote(this.request._id, this.editingNoteId, serialized).subscribe({
            next: (updatedRequest) => {
                this.request = updatedRequest;
                this.editingNoteId = null;
                this.isEditLoading = false;
                this._editInput = null;
                this.requestChanged.emit(updatedRequest);
                this.saveRequest.emit(updatedRequest);
            },
            error: () => {
                this.isEditLoading = false;
                this.cancelEdit();
                this.messageService.error(this.translateService.instant('request_v2.info.internal_notes_edit_error'));
            },
        });
    }

    onEditInput(): void {
        this.detectMentionInElement(this._editInput?.nativeElement);
    }

    onEditKeydown(event: KeyboardEvent): void {
        if (this.showMentionDropdown && event.key === 'Escape') {
            this.closeMentionDropdown();
            event.preventDefault();
            return;
        }
        if (event.key === 'Escape') {
            this.cancelEdit();
            event.preventDefault();
        }
    }

    private descriptionToEditableHtml(description: string): string {
        let html = description.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        html = html.replace(/@\[([^\]:]+):([^\]]+)\]/g, (_, id, name) => {
            if (name === '__deleted__') return '';
            const safeId = id.replace(/["']/g, '');
            const safeName = name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            return `<span class="mention-chip" contenteditable="false" data-id="${safeId}">@${safeName}</span>&nbsp;`;
        });

        html = html.replace(/\n/g, '<br>');

        return html;
    }

    // --- Input handlers ---

    onNoteInput(): void {
        this.detectMention();
    }

    onNoteKeydown(event: KeyboardEvent): void {
        if (this.showMentionDropdown && event.key === 'Escape') {
            this.closeMentionDropdown();
            event.preventDefault();
        }
    }

    onNoteBlur(): void {
        this.blurTimeout = setTimeout(() => this.closeMentionDropdown(), 150);
    }

    onMentionItemMousedown(): void {
        clearTimeout(this.blurTimeout);
    }

    // Strip HTML on paste — only allow plain text
    onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain') || '';
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    }

    // --- Mention detection (contenteditable) ---

    private detectMention(): void {
        this.detectMentionInElement(this.noteInput?.nativeElement);
    }

    private detectMentionInElement(element: HTMLElement | undefined): void {
        if (!element) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const container = range.startContainer;

        if (!element.contains(container)) {
            this.closeMentionDropdown();
            return;
        }

        if (container.nodeType !== Node.TEXT_NODE) {
            this.closeMentionDropdown();
            return;
        }

        const text = container.textContent || '';
        const offset = range.startOffset;
        const textBeforeCursor = text.substring(0, offset);
        const match = textBeforeCursor.match(/(^|[\s\u00A0])@(\w*)$/);

        if (match) {
            this.savedRange = range.cloneRange();
            this.showMentionDropdown = true;
            this.mentionSearchSubject.next(match[2]);
        } else {
            this.closeMentionDropdown();
        }
    }

    private searchMentionAdmins(query: string): void {
        this.mentionLoading = true;
        this.userService
            .searchAdmins(query, 8)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (admins) => {
                    this.mentionSearchResults = (admins as AdminSearchResult[]).map((admin) => ({
                        id: admin.id || admin._id,
                        name: admin.name || `${admin.firstName} ${admin.lastName}`,
                        email: admin.email || '',
                        picture: admin.picture || '',
                        initials: this.getInitials(admin.name || `${admin.firstName} ${admin.lastName}`),
                    }));
                    this.mentionLoading = false;
                },
                error: () => {
                    this.mentionSearchResults = [];
                    this.mentionLoading = false;
                },
            });
    }

    selectMention(admin: AdminUser): void {
        if (!this.savedRange) return;

        const container = this.savedRange.startContainer as Text;
        const offset = this.savedRange.startOffset;
        const textBeforeCursor = (container.textContent || '').substring(0, offset);
        const atIndex = textBeforeCursor.lastIndexOf('@');

        // Delete the typed @query
        const deleteRange = document.createRange();
        deleteRange.setStart(container, atIndex);
        deleteRange.setEnd(container, offset);
        deleteRange.deleteContents();

        // Create non-editable mention chip
        const chip = document.createElement('span');
        chip.className = 'mention-chip';
        chip.contentEditable = 'false';
        chip.dataset.id = admin.id;
        chip.textContent = `@${admin.name}`;

        deleteRange.insertNode(chip);

        // Insert a space after the chip so user can keep typing
        const space = document.createTextNode('\u00A0');
        chip.after(space);

        // Move cursor after the space
        const newRange = document.createRange();
        newRange.setStartAfter(space);
        newRange.collapse(true);
        const sel = window.getSelection();
        if (sel) {
            sel.removeAllRanges();
            sel.addRange(newRange);
        }

        this.closeMentionDropdown();
        const targetInput = this.editingNoteId ? this._editInput?.nativeElement : this.noteInput?.nativeElement;
        if (targetInput) targetInput.focus();
    }

    private closeMentionDropdown(): void {
        this.showMentionDropdown = false;
        this.mentionSearchResults = [];
        this.savedRange = null;
    }

    getRelativeTime(dateStr: string): string {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const diffMs = Date.now() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        const diffH = Math.floor(diffMin / 60);
        const diffD = Math.floor(diffH / 24);
        const fr = this.translateService.currentLang === 'fr';
        if (diffMin < 1) return fr ? "À l'instant" : 'Just now';
        if (diffMin < 60) return fr ? `Il y a ${diffMin} minute${diffMin > 1 ? 's' : ''}` : `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
        if (diffH < 24) return fr ? `Il y a ${diffH} heure${diffH > 1 ? 's' : ''}` : `${diffH} hour${diffH !== 1 ? 's' : ''} ago`;
        if (diffD < 7) return fr ? `Il y a ${diffD} jour${diffD > 1 ? 's' : ''}` : `${diffD} day${diffD !== 1 ? 's' : ''} ago`;
        return date.toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { day: 'numeric', month: 'short', year: 'numeric' });
    }

    private getInitials(name: string): string {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    }
}
